import React from 'react';
import {
  Formik as RealFormik,
  FormikConfig,
  FormikValues,
  FormikProps,
} from 'formik';
import { FormSchemaContext } from './FormSchemaContext';

const visuallyHiddenStyle: React.CSSProperties = {
  position: 'absolute',
  width: '1px',
  height: '1px',
  padding: 0,
  margin: '-1px',
  overflow: 'hidden',
  clip: 'rect(0, 0, 0, 0)',
  whiteSpace: 'nowrap',
  border: 0,
};

const REQUIRED_FIELDS_HINT_TEXT = '* indicates required fields';

const RequiredFieldsHint = (): JSX.Element => (
  <span
    role="alert"
    aria-live="polite"
    aria-atomic="true"
    aria-label={REQUIRED_FIELDS_HINT_TEXT}
    style={visuallyHiddenStyle}
  >
    {REQUIRED_FIELDS_HINT_TEXT}
  </span>
);

export function Formik<V extends FormikValues = FormikValues>(
  props: FormikConfig<V>,
): JSX.Element {
  const { validationSchema, children } = props;
  return (
    <RealFormik {...props}>
      {(formikBag: FormikProps<V>) => (
        <FormSchemaContext.Provider value={validationSchema as never}>
          <RequiredFieldsHint />
          {typeof children === 'function'
            ? (children as (bag: FormikProps<V>) => React.ReactNode)(formikBag)
            : (children as React.ReactNode)}
        </FormSchemaContext.Provider>
      )}
    </RealFormik>
  );
}

export {
  Form,
  Field,
  FieldArray,
  ErrorMessage,
  FormikProvider,
  FormikConsumer,
  FormikContext,
  useFormik,
  useField,
  useFormikContext,
  connect,
  getIn,
  setIn,
  setNestedObjectValues,
  isEmptyArray,
  isEmptyChildren,
  isFunction,
  isObject,
  isInteger,
  isString,
  isNaN,
  isPromise,
  isInputEvent,
  validateYupSchema,
  yupToFormErrors,
  withFormik,
} from 'formik';

export type {
  FormikConfig,
  FormikValues,
  FormikProps,
  FormikHelpers,
  FormikErrors,
  FormikHandlers,
  FormikState,
  FormikTouched,
  FormikSharedConfig,
  FormikComputedProps,
  FormikRegistration,
  FormikContextType,
  FormikBag,
  FieldArrayRenderProps,
  FieldProps,
  FieldHookConfig,
  FieldInputProps,
  FieldMetaProps,
  FieldHelperProps,
  FieldConfig,
  FieldAttributes,
  FieldValidator,
  ArrayHelpers,
  WithFormikConfig,
  InjectedFormikProps,
} from 'formik';

export { FormSchemaProvider, useFieldRequired, FormSchemaContext } from './FormSchemaContext';
